// src/components/employee/EmployeeLeaves.js
import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Lock, UserPlus, AlertCircle, Clock, CheckCircle, XCircle, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useEmployees } from '../../contexts/EmployeeContext';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useLeave } from '../../contexts/LeaveContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { useToast } from '../../contexts/ToastContext';
import holidayPlanService from '../../services/holidayPlan.service';
import notificationService from '../../services/notification.service';
import LeaveRequestModal from '../common/LeaveRequestModal';
import { getISTComponents, formatDate } from '../../utils/dateHelpers';

const EmployeeLeaves = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { employees } = useEmployees();
    const { onboardingData, getCompletionPercentage } = useOnboarding();
    const { addNotification } = useNotifications();
    const toast = useToast();
    const {
        rawLeaveBalance,
        rawLeaveHistory,
        leaveHistory: mappedLeaveHistory,
        fetchLeaveData: refreshLeaveData,
        requestLeave,
        cancelLeave
    } = useLeave();

    const currentEmployee = employees.find(emp => emp.email === user?.email);
    const employeeOnboarding = onboardingData[currentEmployee?.id];
    // Check if admin has approved onboarding
    const isOnboarded = user?.id ? onboardingData[user.id]?.adminReviewed : false;
    const completionPercentage = getCompletionPercentage(currentEmployee?.id);

    const [loading, setLoading] = useState(true);
    const [showRequestModal, setShowRequestModal] = useState(false);
    const [holidays, setHolidays] = useState([]);

    // Optional holiday request state
    const [optionalHolidays, setOptionalHolidays] = useState([]);
    const [requestingHolidayId, setRequestingHolidayId] = useState(null);

    // Fetch leave balance and holidays
    useEffect(() => {
        const loadLeaveData = async () => {
            if (!currentEmployee?.id) {
                setLoading(false);
                return;
            }

            setLoading(true);
            try {
                await refreshLeaveData(true);
                await fetchHolidays();
                await fetchOptionalHolidays();
            } catch (error) {
                console.error('Error loading leave data:', error);
            } finally {
                setLoading(false);
            }
        };

        loadLeaveData();
    }, [currentEmployee?.id, refreshLeaveData]);

    const fetchHolidays = async () => {
        try {
            // Only fetch holidays from the employee's assigned holiday plan
            if (!currentEmployee?.holiday_plan_id) {
                console.warn('No holiday plan assigned to employee');
                setHolidays([]);
                return;
            }

            // Fetch only current year's holidays
            const currentYear = new Date().getFullYear();
            const planDetails = await holidayPlanService.getHolidayPlanById(currentEmployee.holiday_plan_id, currentYear);

            if (!planDetails?.holidays) {
                setHolidays([]);
                return;
            }

            // Fetch approved optional holidays for this employee
            let approvedOptionalHolidayIds = new Set();
            try {
                const myRequests = await holidayPlanService.getMyOptionalHolidayRequests();
                approvedOptionalHolidayIds = new Set(
                    myRequests
                        .filter(r => r.status === 'approved')
                        .map(r => r.holiday_date_id)
                );
            } catch (err) {
                console.warn('Could not fetch approved optional holidays:', err);
            }

            // Filter holidays: include non-optional + approved optional holidays
            // Also filter to current year only (in case backend returns all years)
            const filteredHolidays = planDetails.holidays.filter(holiday => {
                // Filter by current year
                const holidayYear = new Date(holiday.date).getFullYear();
                if (holidayYear !== currentYear) {
                    return false;
                }

                const isOptional = holiday.is_optional || holiday.isOptional;
                if (!isOptional) {
                    return true; // Non-optional holidays are always shown
                }
                // Optional holidays only shown if approved
                return approvedOptionalHolidayIds.has(holiday.id);
            });

            // Sort by date
            filteredHolidays.sort((a, b) => {
                const dateA = a.date.split('T')[0];
                const dateB = b.date.split('T')[0];
                return dateA.localeCompare(dateB);
            });

            setHolidays(filteredHolidays);
        } catch (error) {
            console.error('Error fetching holidays:', error);
            setHolidays([]);
        }
    };

    const fetchOptionalHolidays = async () => {
        try {
            const holidays = await holidayPlanService.getAvailableOptionalHolidays();
            // Filter to show only current year's optional holidays
            const currentYear = new Date().getFullYear();
            const currentYearHolidays = holidays.filter(holiday => {
                const holidayYear = new Date(holiday.date).getFullYear();
                return holidayYear === currentYear;
            });
            setOptionalHolidays(currentYearHolidays);
        } catch (error) {
            console.error('Error fetching optional holidays:', error);
            setOptionalHolidays([]);
        }
    };

    const handleRequestOptionalHoliday = async (holidayDateId) => {
        try {
            setRequestingHolidayId(holidayDateId);
            const response = await holidayPlanService.requestOptionalHoliday(holidayDateId);
            if (response.success) {
                toast.success(response.message || 'Optional holiday request submitted successfully!');
                await fetchOptionalHolidays(); // Refresh to show updated status
            } else {
                toast.error(response.message || 'Failed to submit request');
            }
        } catch (error) {
            console.error('Error requesting optional holiday:', error);
            const errorMsg = error.response?.data?.error || error.message || 'Failed to submit request';
            toast.error(errorMsg);
        } finally {
            setRequestingHolidayId(null);
        }
    };

    const handleCancelOptionalHolidayRequest = async (requestId) => {
        if (!window.confirm('Are you sure you want to cancel this optional holiday request?')) {
            return;
        }

        try {
            setRequestingHolidayId(requestId);
            const response = await holidayPlanService.cancelOptionalHolidayRequest(requestId);
            if (response.success) {
                toast.success('Request cancelled successfully');
                await fetchOptionalHolidays();
            } else {
                toast.error(response.message || 'Failed to cancel request');
            }
        } catch (error) {
            console.error('Error cancelling optional holiday request:', error);
            toast.error(error.response?.data?.error || 'Failed to cancel request');
        } finally {
            setRequestingHolidayId(null);
        }
    };

    const myBalances = rawLeaveBalance?.balance || rawLeaveBalance?.balances || [];
    console.log('🎯 myBalances for dropdown:', myBalances, 'rawLeaveBalance:', rawLeaveBalance);
    const historySource = (rawLeaveHistory && rawLeaveHistory.length > 0 ? rawLeaveHistory : mappedLeaveHistory) || [];
    const myLeaveRequests = [...historySource].sort((a, b) => {
        const getDate = (record) => new Date(
            record.created_at ||
            record.applied_date ||
            record.from_date ||
            record.start_date ||
            record.appliedOn ||
            record.fromDate ||
            0
        );
        return getDate(b) - getDate(a);
    });
    const leaveBalanceMessage = rawLeaveBalance?.message || rawLeaveBalance?.note;

    const calculateLeaveDays = (fromDate, toDate, isHalfDay) => {
        if (!fromDate || !toDate) return 0;
        const start = new Date(fromDate);
        const end = new Date(toDate);
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
            return 0;
        }
        const diffTime = end.getTime() - start.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        const total = diffDays >= 0 ? diffDays + 1 : 1;
        return isHalfDay ? 0.5 : total;
    };

    const handleLeaveRequest = async (requestData) => {
        if (!currentEmployee?.id) return;

        try {
            setLoading(true);
            console.log('🔍 Raw leave request data from modal:', requestData);

            const totalDays = calculateLeaveDays(requestData.fromDate, requestData.toDate, requestData.isHalfDay);
            const processedData = {
                leaveTypeId: requestData.leaveTypeId,
                fromDate: requestData.fromDate,
                toDate: requestData.toDate,
                days: totalDays,
                reason: requestData.reason,
                isHalfDay: requestData.isHalfDay,
                emergencyContact: requestData.emergencyContact
            };

            console.log('✅ Processed leave data before API call:', processedData);
            const response = await requestLeave(processedData);
            console.log('📋 API response:', response);

            if (response.success) {
                await refreshLeaveData(true);

                // Send notification to employee (confirmation)
                await addNotification({
                    userId: user.id,
                    type: 'leave_submitted',
                    title: 'Leave Request Submitted',
                    message: `Your ${requestData.leaveTypeName || 'leave'} request for ${totalDays} day(s) from ${formatDate(requestData.fromDate)} to ${formatDate(requestData.toDate)} has been submitted successfully and is pending approval.`,
                    icon: 'Calendar',
                    color: 'blue',
                    data: {
                        leave_id: response.data?.id || response.id,
                        leave_type: requestData.leaveTypeName,
                        from_date: requestData.fromDate,
                        to_date: requestData.toDate,
                        days: totalDays
                    }
                });

                // Send notification to manager/approver
                if (currentEmployee?.reporting_manager_id) {
                    await addNotification({
                        userId: currentEmployee.reporting_manager_id,
                        type: 'team_leave_request',
                        title: 'New Leave Request',
                        message: `${user.name || user.email} has requested ${requestData.leaveTypeName || 'leave'} for ${totalDays} day(s) from ${formatDate(requestData.fromDate)} to ${formatDate(requestData.toDate)}. Reason: ${requestData.reason}`,
                        icon: 'Calendar',
                        color: 'blue',
                        data: {
                            leave_id: response.data?.id || response.id,
                            employee_id: currentEmployee.id,
                            employee_name: user.name || user.email,
                            leave_type: requestData.leaveTypeName,
                            from_date: requestData.fromDate,
                            to_date: requestData.toDate,
                            days: totalDays,
                            reason: requestData.reason
                        }
                    });
                }

                setShowRequestModal(false);
                toast.success(response.message || 'Leave request submitted successfully! Your leave balance has been updated.');
            } else {
                // Show error message from backend
                const errorMsg = response.message || 'Failed to submit leave request';
                toast.error(errorMsg, 'Leave Request Failed');
                throw new Error(errorMsg);
            }
        } catch (error) {
            console.error('❌ Error submitting leave request:', error);
            // Extract the most user-friendly error message
            let errorMessage = 'Error submitting leave request. Please try again.';

            if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }

            // Show error in toast notification
            toast.error(errorMessage, 'Leave Request Failed');

            // Re-throw to prevent modal from closing
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const handleCancelRequest = async (requestId) => {
        if (!currentEmployee?.id) return;

        if (!window.confirm('Are you sure you want to cancel this leave request?')) {
            return;
        }

        try {
            setLoading(true);
            const response = await cancelLeave(requestId);
            if (response.success) {
                await refreshLeaveData(true);
                toast.success(response.message || 'Leave request cancelled successfully!', 'Leave Cancelled');
            } else {
                toast.error(response.message || 'Error cancelling leave request. Please try again.', 'Cancellation Failed');
            }
        } catch (error) {
            console.error('Error cancelling leave request:', error);
            const errorMessage = error.response?.data?.error || error.message || 'Error cancelling leave request. Please try again.';
            toast.error(errorMessage, 'Cancellation Failed');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <div className="flex justify-center items-center h-64 text-gray-900 dark:text-white">Loading...</div>;
    }

    // Onboarding lock removed - all employees can access leave management
    if (false && !isOnboarded) {
        return (
            <div className="space-y-6">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Leave Management</h1>

                {/* Onboarding Required Banner */}
                <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 dark:border-red-500 p-6 rounded-lg">
                    <div className="flex items-start">
                        <Lock className="w-6 h-6 text-red-600 dark:text-red-400 mt-1" />
                        <div className="ml-4 flex-1">
                            <h2 className="text-lg font-semibold text-red-900 dark:text-red-200 mb-2">
                                Complete Your Onboarding to Apply for Leaves
                            </h2>
                            <p className="text-red-700 dark:text-red-300 mb-4">
                                You need to complete your onboarding process before you can apply for leaves.
                                Please provide all required information and upload necessary documents.
                            </p>

                            {/* Progress Bar */}
                            <div className="mb-4">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-red-900 dark:text-red-200">Onboarding Progress</span>
                                    <span className="text-sm font-bold text-red-900 dark:text-red-200">{completionPercentage}%</span>
                                </div>
                                <div className="w-full bg-red-200 dark:bg-red-800/30 rounded-full h-3">
                                    <div
                                        className="bg-red-600 dark:bg-red-500 h-3 rounded-full transition-all duration-300"
                                        style={{ width: `${completionPercentage}%` }}
                                    />
                                </div>
                            </div>

                            {/* Missing Items */}
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 mb-4">
                                <h3 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                    <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                                    What's Pending?
                                </h3>
                                <ul className="space-y-2 text-sm">
                                    {completionPercentage < 100 && (
                                        <>
                                            <li className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                                                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                                                Complete all required information fields
                                            </li>
                                            <li className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                                                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                                                Upload all required documents
                                            </li>
                                        </>
                                    )}
                                    {completionPercentage === 100 && !employeeOnboarding?.adminReviewed && (
                                        <li className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                            Awaiting HR admin review and approval
                                        </li>
                                    )}
                                </ul>
                            </div>

                            <button
                                onClick={() => navigate('/employee/onboarding')}
                                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 font-medium transition-colors"
                            >
                                <UserPlus className="w-5 h-5" />
                                Complete Onboarding Now
                            </button>
                        </div>
                    </div>
                </div>

                {/* Locked Content Preview */}
                <div className="relative">
                    <div className="absolute inset-0 bg-gray-100 dark:bg-gray-800 bg-opacity-90 backdrop-blur-sm z-10 rounded-lg flex items-center justify-center">
                        <div className="text-center">
                            <Lock className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
                            <p className="text-gray-600 dark:text-gray-300 font-medium">This section is locked</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Complete onboarding to access</p>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 opacity-50">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Leave Balances</h2>
                            <button disabled className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 rounded-lg cursor-not-allowed flex items-center gap-2">
                                <Plus className="w-4 h-4" />
                                Apply Leave
                            </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded text-center">
                                <p className="text-gray-400 dark:text-gray-500">Annual Leave</p>
                                <p className="text-2xl font-bold text-gray-400 dark:text-gray-500">--</p>
                            </div>
                            <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded text-center">
                                <p className="text-gray-400 dark:text-gray-500">Sick Leave</p>
                                <p className="text-2xl font-bold text-gray-400 dark:text-gray-500">--</p>
                            </div>
                            <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded text-center">
                                <p className="text-gray-400 dark:text-gray-500">Casual Leave</p>
                                <p className="text-2xl font-bold text-gray-400 dark:text-gray-500">--</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Leave Management</h1>
                <button
                    onClick={() => setShowRequestModal(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                    <Plus className="w-4 h-4" />
                    Apply Leave
                </button>
            </div>

            {/* Leave Balances - Above Leave Requests */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Leave Balance</h2>
                {myBalances.length > 0 ? (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {myBalances.map((balance, index) => {
                                const colors = [
                                    { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-700 dark:text-blue-300', bold: 'text-blue-900 dark:text-blue-100' },
                                    { bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-700 dark:text-green-300', bold: 'text-green-900 dark:text-green-100' },
                                    { bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-700 dark:text-purple-300', bold: 'text-purple-900 dark:text-purple-100' },
                                    { bg: 'bg-indigo-50 dark:bg-indigo-900/20', text: 'text-indigo-700 dark:text-indigo-300', bold: 'text-indigo-900 dark:text-indigo-100' },
                                    { bg: 'bg-pink-50 dark:bg-pink-900/20', text: 'text-pink-700 dark:text-pink-300', bold: 'text-pink-900 dark:text-pink-100' },
                                    { bg: 'bg-yellow-50 dark:bg-yellow-900/20', text: 'text-yellow-700 dark:text-yellow-300', bold: 'text-yellow-900 dark:text-yellow-100' },
                                    { bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-700 dark:text-red-300', bold: 'text-red-900 dark:text-red-100' },
                                    { bg: 'bg-gray-50 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-300', bold: 'text-gray-900 dark:text-gray-100' }
                                ];
                                const color = colors[index % colors.length];
                                // Backend returns: name, total_days, used_days, remaining_days
                                const leaveType = balance.name || balance.leave_type;
                                const total = balance.total_days || balance.total || 0;
                                const used = balance.used_days || balance.used || 0;
                                const available = balance.remaining_days || (total - used);

                                return (
                                    <div key={leaveType} className={`text-center p-4 ${color.bg} rounded-lg`}>
                                        <p className={`text-xs font-medium ${color.text} mb-1`}>{leaveType}</p>
                                        <p className={`text-2xl font-bold ${color.bold}`}>{available}/{total}</p>
                                        <p className={`text-xs ${color.text} mt-1`}>{used} used</p>
                                    </div>
                                );
                            })}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                            * Leave balances show available/total days
                        </p>
                    </>
                ) : (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        <p className="text-base font-medium mb-2">
                            {leaveBalanceMessage || 'No leave balance information available'}
                        </p>
                        <p className="text-sm">
                            {leaveBalanceMessage
                                ? 'Please contact HR to set up your leave plan'
                                : 'Please contact HR to set up your leave plan'}
                        </p>
                    </div>
                )}
            </div>

            {/* Leave Requests */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">My Leave Requests</h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Type</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">From</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">To</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Days</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Reason</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
                        </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {myLeaveRequests?.length === 0 ? (
                            <tr>
                                <td colSpan="7" className="px-6 py-8 text-center">
                                    <div className="flex flex-col items-center justify-center">
                                        <Calendar className="w-12 h-12 text-gray-400 dark:text-gray-500 mb-3" />
                                        <p className="text-gray-500 dark:text-gray-400 text-sm">No leave requests yet</p>
                                        <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">Click "Apply Leave" to submit your first request</p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            myLeaveRequests.map((request) => (
                                <tr key={request.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">{request.leave_type}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                                        {formatDate(request.from_date)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                                        {formatDate(request.to_date)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">{request.days_requested}</td>
                                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300 max-w-xs truncate" title={request.reason}>{request.reason}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full capitalize ${
                                            request.status === 'approved' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                                                request.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' :
                                                    request.status === 'rejected' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' :
                                                        'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                        }`}>
                                          {request.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        {request.status === 'pending' && (
                                            <button
                                                onClick={() => handleCancelRequest(request.id)}
                                                className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 font-medium"
                                            >
                                                Cancel
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Optional Holidays - Employee can request */}
            {optionalHolidays.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Optional Holidays</h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Request optional holidays from your holiday plan</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {optionalHolidays.map((holiday) => {
                            const getStatusIcon = () => {
                                if (holiday.request_status === 'approved') {
                                    return <CheckCircle className="w-5 h-5 text-green-500" />;
                                }
                                if (holiday.request_status === 'pending') {
                                    return <Clock className="w-5 h-5 text-amber-500" />;
                                }
                                if (holiday.request_status === 'rejected') {
                                    return <XCircle className="w-5 h-5 text-red-500" />;
                                }
                                return null;
                            };

                            const getStatusBadge = () => {
                                if (holiday.request_status === 'approved') {
                                    return (
                                        <span className="px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 text-xs rounded-full">
                                            Approved
                                        </span>
                                    );
                                }
                                if (holiday.request_status === 'pending') {
                                    return (
                                        <span className="px-2 py-1 bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 text-xs rounded-full">
                                            Pending Approval
                                        </span>
                                    );
                                }
                                if (holiday.request_status === 'rejected') {
                                    return (
                                        <div>
                                            <span className="px-2 py-1 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 text-xs rounded-full">
                                                Rejected
                                            </span>
                                            {holiday.rejection_reason && (
                                                <p className="text-xs text-red-500 mt-1">{holiday.rejection_reason}</p>
                                            )}
                                        </div>
                                    );
                                }
                                return null;
                            };

                            return (
                                <div
                                    key={holiday.id}
                                    className={`p-4 border rounded-lg ${
                                        holiday.request_status === 'approved'
                                            ? 'border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/10'
                                            : holiday.request_status === 'pending'
                                            ? 'border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/10'
                                            : holiday.request_status === 'rejected'
                                            ? 'border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/10'
                                            : 'border-gray-200 dark:border-gray-700'
                                    }`}
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <p className="font-medium text-gray-900 dark:text-white">{holiday.name}</p>
                                                {getStatusIcon()}
                                            </div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                                {(() => {
                                                    const { year, month, day, dayOfWeek } = getISTComponents(holiday.date);
                                                    const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                                    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                                                    // month from getISTComponents is 1-12, so subtract 1 for array index
                                                    return `${weekdays[dayOfWeek] || 'Unknown'}, ${months[month - 1] || 'Unknown'} ${day}, ${year}`;
                                                })()}
                                            </p>
                                            {holiday.description && (
                                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{holiday.description}</p>
                                            )}
                                            <div className="mt-2">
                                                {getStatusBadge()}
                                            </div>
                                        </div>
                                        <div className="ml-4">
                                            {!holiday.request_status ? (
                                                <button
                                                    onClick={() => handleRequestOptionalHoliday(holiday.id)}
                                                    disabled={requestingHolidayId === holiday.id}
                                                    className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                                                >
                                                    <Send className="w-3 h-3" />
                                                    Request
                                                </button>
                                            ) : holiday.request_status === 'pending' ? (
                                                <button
                                                    onClick={() => handleCancelOptionalHolidayRequest(holiday.request_id)}
                                                    disabled={requestingHolidayId === holiday.request_id}
                                                    className="px-3 py-1.5 bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 text-sm rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                                                >
                                                    Cancel
                                                </button>
                                            ) : holiday.request_status === 'rejected' ? (
                                                <button
                                                    onClick={() => handleRequestOptionalHoliday(holiday.id)}
                                                    disabled={requestingHolidayId === holiday.id}
                                                    className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                                                >
                                                    <Send className="w-3 h-3" />
                                                    Re-request
                                                </button>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* This Year Holidays */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">This Year Holidays</h2>

                {/* Holidays List */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {holidays?.length > 0 ? (
                        holidays.map((holiday) => (
                            <div key={holiday.id} className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                                <div>
                                    <p className="font-medium text-gray-900 dark:text-white">{holiday.name}</p>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        {(() => {
                                            const { year, month, day, dayOfWeek } = getISTComponents(holiday.date);
                                            const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                            const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                                            // month from getISTComponents is 1-12, so subtract 1 for array index
                                            return `${weekdays[dayOfWeek] || 'Unknown'}, ${months[month - 1] || 'Unknown'} ${day}, ${year}`;
                                        })()}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    {holiday.is_optional && (
                                        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 text-xs rounded">
                                            Optional
                                        </span>
                                    )}
                                    <Calendar className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="col-span-2 text-center text-gray-500 dark:text-gray-400 py-4">
                            No holidays for this year
                        </div>
                    )}
                </div>
            </div>

            {/* Leave Request Modal */}
            {showRequestModal && (
                <LeaveRequestModal
                    leaveTypes={myBalances}
                    onClose={() => setShowRequestModal(false)}
                    onSubmit={handleLeaveRequest}
                />
            )}
        </div>
    );
};

export default EmployeeLeaves;
